import { Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { SilentRequest } from "@azure/msal-browser";
import { Observable, of, from } from "rxjs";
import { map, catchError, shareReplay, finalize } from "rxjs/operators";
import { SecureStorageService } from "./securestorage.service";
import { ApplicationConstants } from "@constants/app.constants";

@Injectable({ providedIn: 'root' })
export class AuthTokenService {
    private readonly tokenCache = new Map<string, { token: string; expiresOn: number }>();
    private readonly inFlight = new Map<string, Observable<string | null>>();    

    constructor(
        private readonly msalService: MsalService,
        private readonly sessionService: SecureStorageService // Your custom session storage wrapper
    ) { }

    getToken(scopeKey: string, silentRequest: SilentRequest, isGraph: boolean): Observable<string | null> {
        const cached = this.tokenCache.get(scopeKey);
        const now = Math.floor(Date.now() / 1000);
        // if cached token exists and not expiring within 5 minutes, return it
        if (cached && cached.expiresOn > now + 300) {
            return of(cached.token);
        }
        return this.acquireTokenSingleShot(silentRequest, scopeKey, isGraph);
    }

    private acquireTokenSingleShot(silentRequest: SilentRequest, scopeKey: string, isGraph: boolean): Observable<string | null> {
        const existing = this.inFlight.get(scopeKey);
        if (existing) return existing;

        const obs = from(this.msalService.acquireTokenSilent(silentRequest)).pipe(            
            map((result: any) => {
                const token = result?.accessToken ?? null;
                if (token) {
                    let expiresOn = Math.floor(Date.now() / 1000) + 3600;
                    try {
                        if (result.expiresOn) {
                            expiresOn = Math.floor(new Date(result.expiresOn).getTime() / 1000);
                        }
                    } catch (e) {
                        console.warn('Error parsing expiresOn:', e);
                    }

                    this.tokenCache.set(scopeKey, { token, expiresOn });

                    try {
                        const key = isGraph ? 'graphAccessToken' : 'accessToken';
                        const prev = this.sessionService.getSessionStorageData(key);
                        if (prev !== token) {
                            this.sessionService.saveSessionStorageData(key, token);
                        }
                    } catch (e) {
                        console.warn('Session storage error:', e);
                    }
                }
                return token;
            }),
            catchError((err) => {
                // If MSAL hasn't finished initialize() we can get a known BrowserAuthError.
                // Treat it as a benign race condition and return null so callers fall back to storage.
                const msg = err?.errorCode || err?.name || err?.message || '';
                if (typeof msg === 'string' && msg.includes('uninitialized_public_client_application')) {
                    console.debug('MSAL not initialized yet - skipping acquireTokenSilent.');
                    return of(null);
                }
                console.error('Token acquisition failed:', err);
                return of(null);
            }),
            shareReplay(1),
            finalize(() => this.inFlight.delete(scopeKey))
        );

        this.inFlight.set(scopeKey, obs);
        return obs;
    }

    getTokenFromStorage(isGraph: boolean): string | null {
        return isGraph
            ? this.sessionService.getSessionStorageData('graphAccessToken')
            : this.sessionService.getSessionStorageData('accessToken');
    }

    getScopeKey(scopes: string[] | undefined): string {
        if (!scopes || scopes.length === 0) return '';
        return scopes.slice().sort((a, b) => a.localeCompare(b, 'en')).join('|');
    }

    getProjectId(): string {
        return this.sessionService.getSessionStorageData("projectId");
    }

    /** Project role for the active project if assigned, otherwise the user's global roles (comma-separated). */
    getUserRole(): string {
        const projectId = this.getProjectId();
        const projectRoles = this.sessionService.getSessionStorageData('projectRoleInfo');
        if (projectId && Array.isArray(projectRoles)) {
            const projectRole = projectRoles.find((r) => r?.ProjectId == projectId)?.RoleName;
            if (projectRole) {
                return this.toHeaderSafe(projectRole);
            }
        }

        const globalRoles = this.sessionService.getSessionStorageData('roleInfo');
        if (!Array.isArray(globalRoles)) {
            return '';
        }
        return this.toHeaderSafe(
            globalRoles
                .map((r) => (typeof r === 'string' ? r : r?.RoleName))
                .filter(Boolean)
                .join(',')
        );
    }

    // Header values must be printable ASCII; drop anything else to avoid request errors/header injection.
    private toHeaderSafe(value: string): string {
        return String(value).replace(/[^\x20-\x7E]/g, '').trim();
    }

    isGraphApiUrl(url: string): boolean {
        try {
            const parsedUrl = new URL(url);
            const allowedHosts = ['graph.microsoft.com'];
            return allowedHosts.includes(parsedUrl.host);
        } catch (e) {
            console.warn('Error parsing URL in isGraphApiUrl:', e);
            return false;
        }
    }

    getActiveModule(): string {    
        if(this.sessionService.getSessionStorageData('activeModule') == null){
            let module = this.sessionService.getLocalStorageData('module');
            if (module) {
                this.sessionService.saveSessionStorageData('activeModule', module);
            }
            else{
                module = ApplicationConstants.PMPRO;
                this.sessionService.saveSessionStorageData('activeModule', module);
            }
        } 
        return this.sessionService.getSessionStorageData('activeModule');
    }
}
